import { useCallback, useMemo, useRef, useState } from 'react';
import { addDays, isAfter, isBefore, isSameDay, subDays, differenceInCalendarDays } from 'date-fns';
import { useConfig } from '../../../../context';
import { getDateWithoutTimezone } from '../../../../Engine/components/FastSearch/utils';
import { API_DATE_FORMAT, format } from '../../../../utils';
import { useMinPricesInPeriod } from './useMinPricesInPeriod';
function isAfterOrSame(date, dateToCompare) {
    return isAfter(date, dateToCompare) || isSameDay(date, dateToCompare);
}
function isBeforeOrSame(date, dateToCompare) {
    return isBefore(date, dateToCompare) || isSameDay(date, dateToCompare);
}
export function useWeekCalendarMinPrices({ legId, searchParameters, skip }) {
    var _a;
    const { calendarDaysCount, RTMinPricesForOWFlights: isRTMinPricesForOWFlights } = (_a = useConfig()) === null || _a === void 0 ? void 0 : _a.Engine;
    const prevLegIdRef = useRef(legId);
    const [calendarPage, setCalendarPage] = useState(0);
    const effectiveCalendarPage = useMemo(() => {
        if (prevLegIdRef.current !== legId) {
            prevLegIdRef.current = legId;
            setCalendarPage(0);
            return 0;
        }
        return calendarPage;
    }, [legId, calendarPage]);
    const { calendarDaysOffsetCount, daysOffset } = useMemo(() => ({
        calendarDaysOffsetCount: calendarDaysCount * effectiveCalendarPage,
        daysOffset: Math.floor(calendarDaysCount / 2)
    }), [calendarDaysCount, effectiveCalendarPage]);
    const calendarParams = useMemo(() => {
        const uniqueDateSet = new Set();
        let centralDate;
        let startDate;
        let endDate;
        const segments = searchParameters.segments.map((segment, index) => {
            uniqueDateSet.add(segment.date);
            const segmentDate = getDateWithoutTimezone(segment.date);
            const startWeekSegmentDate = subDays(segmentDate, daysOffset);
            // Current segment
            if (index === legId) {
                const offsetDate = addDays(startWeekSegmentDate, calendarDaysOffsetCount);
                centralDate = addDays(offsetDate, daysOffset);
                startDate = subDays(centralDate, daysOffset);
                endDate = addDays(centralDate, daysOffset);
                return Object.assign(Object.assign({}, segment), { date: format(offsetDate, API_DATE_FORMAT) });
            }
            return Object.assign(Object.assign({}, segment), { date: format(startWeekSegmentDate, API_DATE_FORMAT) });
        });
        if (segments.length === 1 && isRTMinPricesForOWFlights) {
            const firstSegment = segments[0];
            segments.push(Object.assign(Object.assign({}, firstSegment), { departure: firstSegment.arrival, arrival: firstSegment.departure }));
            return { centralDate, startDate, endDate, segments };
        }
        if (effectiveCalendarPage !== 0 && uniqueDateSet.size > 1) {
            const isFirstLeg = legId === 0;
            const targetSegmentIndex = isFirstLeg ? segments.length - 1 : 0;
            const targetSegment = segments[targetSegmentIndex];
            const targetSegmentStartDate = getDateWithoutTimezone(targetSegment.date);
            const targetSegmentEndDate = addDays(targetSegmentStartDate, calendarDaysCount - 1);
            const shouldAdjustTargetSegment = isFirstLeg
                ? isAfterOrSame(endDate, targetSegmentStartDate)
                : isBeforeOrSame(startDate, targetSegmentEndDate);
            if (shouldAdjustTargetSegment) {
                const diffDays = isFirstLeg
                    ? differenceInCalendarDays(endDate, targetSegmentStartDate) + 1
                    : differenceInCalendarDays(startDate, targetSegmentEndDate) - 1;
                const diffWeeks = diffDays >= 0 ? Math.ceil(diffDays / calendarDaysCount) : Math.floor(diffDays / calendarDaysCount);
                const offsetDaysCount = calendarDaysCount * diffWeeks;
                segments[targetSegmentIndex] = Object.assign(Object.assign({}, targetSegment), { date: format(addDays(targetSegmentStartDate, offsetDaysCount), API_DATE_FORMAT) });
            }
        }
        return { centralDate, startDate, endDate, segments };
    }, [
        searchParameters.segments,
        legId,
        daysOffset,
        calendarDaysCount,
        calendarDaysOffsetCount,
        effectiveCalendarPage
    ]);
    const minPrices = useMinPricesInPeriod({
        variables: {
            parameters: Object.assign(Object.assign({}, searchParameters), { segments: calendarParams.segments, daysCount: calendarDaysCount })
        },
        context: { currency: searchParameters.currency },
        skip: skip
    });
    const nextCalendarPage = useCallback(() => {
        setCalendarPage(prevCalendarPage => prevCalendarPage + 1);
    }, []);
    const prevCalendarPage = useCallback(() => {
        setCalendarPage(prevCalendarPage => prevCalendarPage - 1);
    }, []);
    return Object.assign(Object.assign({}, minPrices), { calendarPage: effectiveCalendarPage, calendarParams,
        nextCalendarPage,
        prevCalendarPage });
}
